import React, { useState, useEffect } from 'react';
import { X, Shield, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../ui/Button';
import { PERMISSION_CATEGORIES } from '../../lib/permissions';

interface ManagePermissionsModalProps {
  user: {
    id: string;
    email: string;
    username: string;
    is_super_admin: boolean;
  };
  onClose: () => void;
  onSuccess: () => void;
}

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface UserPermission {
  permission_id: string;
  granted: boolean;
}

const ManagePermissionsModal: React.FC<ManagePermissionsModalProps> = ({ user, onClose, onSuccess }) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userPermissions, setUserPermissions] = useState<Map<string, boolean>>(new Map());
  const [rolePermissions, setRolePermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [permissionsResult, userPermissionsResult, userRolesResult] = await Promise.all([
        supabase.from('permissions').select('*').order('category').order('name'),
        supabase.from('user_permissions').select('*').eq('user_id', user.id),
        supabase.from('user_roles').select('role_id').eq('user_id', user.id),
      ]);

      if (permissionsResult.error) throw permissionsResult.error;
      setPermissions(permissionsResult.data || []);

      const userPermsMap = new Map<string, boolean>();
      (userPermissionsResult.data || []).forEach((up: UserPermission) => {
        userPermsMap.set(up.permission_id, up.granted);
      });
      setUserPermissions(userPermsMap);

      const roleIds = (userRolesResult.data || []).map(ur => ur.role_id);
      if (roleIds.length > 0) {
        const { data: rolePerms } = await supabase
          .from('role_permissions')
          .select('permission_id')
          .in('role_id', roleIds);

        const rolePermSet = new Set<string>();
        (rolePerms || []).forEach(rp => rolePermSet.add(rp.permission_id));
        setRolePermissions(rolePermSet);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permissionId: string): boolean => {
    if (user.is_super_admin) return true;

    const customPerm = userPermissions.get(permissionId);
    if (customPerm !== undefined) return customPerm;

    return rolePermissions.has(permissionId);
  };

  const togglePermission = (permissionId: string) => {
    if (user.is_super_admin) return;

    const currentValue = userPermissions.get(permissionId);
    const roleHas = rolePermissions.has(permissionId);

    if (currentValue === undefined) {
      setUserPermissions(new Map(userPermissions.set(permissionId, !roleHas)));
    } else {
      setUserPermissions(new Map(userPermissions.set(permissionId, !currentValue)));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error: deleteError } = await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      const permissionsToInsert: any[] = [];
      userPermissions.forEach((granted, permissionId) => {
        const roleHas = rolePermissions.has(permissionId);
        if (granted !== roleHas) {
          permissionsToInsert.push({
            user_id: user.id,
            permission_id: permissionId,
            granted,
          });
        }
      });

      if (permissionsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('user_permissions')
          .insert(permissionsToInsert);

        if (insertError) throw insertError;
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Shield className="h-5 w-5 text-blue-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Manage Permissions</h2>
              <p className="text-sm text-gray-600">{user.username} ({user.email})</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {user.is_super_admin ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <Shield className="h-8 w-8 text-blue-900 mx-auto mb-2" />
              <p className="text-blue-900 font-medium">Super Administrator</p>
              <p className="text-blue-700 text-sm mt-1">
                This user has full access to all features and permissions
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">How Permissions Work</h3>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Checked permissions are granted to the user</li>
                  <li>Permissions from roles are shown with a role badge</li>
                  <li>You can override role permissions by explicitly granting or revoking them</li>
                </ul>
              </div>

              {Object.entries(groupedPermissions).map(([category, perms]) => (
                <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">{category}</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {perms.map((perm) => {
                      const hasIt = hasPermission(perm.id);
                      const fromRole = rolePermissions.has(perm.id);
                      const isCustom = userPermissions.has(perm.id);

                      return (
                        <div
                          key={perm.id}
                          className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => togglePermission(perm.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <div
                                className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  hasIt
                                    ? 'bg-blue-900 border-blue-900'
                                    : 'border-gray-300 bg-white'
                                }`}
                              >
                                {hasIt && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{perm.description}</p>
                                  {fromRole && !isCustom && (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                      From Role
                                    </span>
                                  )}
                                  {isCustom && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                      Custom
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{perm.name}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            {!user.is_super_admin && (
              <Button
                type="button"
                onClick={handleSave}
                isLoading={saving}
                fullWidth
              >
                Save Permissions
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagePermissionsModal;
